import { SearchOutlined } from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
import { Spiner } from "@components/loaders/spiner";
import { Product } from "@services/dtos/marketplace.dto";
import { useThemeStore } from "@stores/theme.store";
import { useQuery } from "@tanstack/react-query";
import { db } from "@utils/database";
import { toRp } from "@utils/helper";
import { linkPage } from "@utils/router";
import { Badge, Button, Form, Input, message, Select } from "antd";
import { NoticeType } from "antd/es/message/interface";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import { FaCartPlus, FaFilter, FaList, FaWhatsapp } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { HiBuildingStorefront } from "react-icons/hi2";
import Masonry from "react-masonry-css";
import { useNavigate } from "react-router-dom";
import { MarketplaceService } from "@services/marketplace.service";
import moment from "moment";

const MarketplaceProductPage = () => {
  const navigate = useNavigate();
  const { setHeader, setAppBar, setBottomNavigation } = useThemeStore(
    (state) => state
  );
  const [imgLoaded, setImgLoaded] = useState(false);
  const [clampedId, setClampedId] = useState<number | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);
  const [popupImg, setPopupImg] = useState<boolean>(false);
  const [selectedImg, setSelectedImg] = useState<string>("");
  const [jenis, setJenis] = useState<string>("*");
  const [toko, setToko] = useState<string>("*");
  const [keyword, setKeyword] = useState<string>("");

  const {
    data: dataArray,
    isPending,
    refetch,
  } = useQuery({
    queryKey: [
      MarketplaceService.queries.GET_DATA_PRODUCT,
      jenis,
      toko,
      keyword,
    ],
    queryFn: () => MarketplaceService.products(jenis, toko, keyword),
    gcTime: 3 * 60 * 1000,
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const data = dataArray?.data ?? [];

  const { data: dataMerchant } = useQuery({
    queryKey: [MarketplaceService.queries.GET_MARKETPLACE_MERCHANT],
    queryFn: () => MarketplaceService.merchant(),
    gcTime: 3 * 60 * 1000,
    staleTime: 3 * 60 * 1000,
  });

  useEffect(() => {
    refetch();
    setAppBar({
      title: "Toko",
      show: true,
      checkout: true,
      shopList: true,
      leading: "back",
    });
    setHeader({
      buttonBack: true,
      title: "Toko",
      filter: false,
      image: false,
    });
    setBottomNavigation("home");
  }, [setHeader, setAppBar, setBottomNavigation]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText]);

  const onAddOnCart = (product: Product) => {
    db.carts
      .add({
        id: Number(product.id),
        check: false,
        name: product.productName,
        picture: product.productImage,
        price: product.sellPrice,
        qty: 1,
        message: "",
        stockReady: product.stockReady,
        openTime: product.openTime,
        closeTime: product.closeTime,
      })
      .then(() => {
        setShowMessage(true);
        setMessageType("success");
        setShowMessageText(`Ditambahkan ke keranjang`);
      })
      .catch((e) => {
        if (e.message.includes("already exists")) {
          setShowMessage(true);
          setMessageType("info");
          setShowMessageText(`produk sudah ada di keranjang`);
          return;
        }
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(`gagal ditambahkan ke keranjang`);
      });
  };

  const carts = useLiveQuery(() => db.carts.toArray());

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        <Form>
          <Form.Item name="cari" className="tw-mb-3">
            <Input
              prefix={<SearchOutlined />}
              className="tw-rounded-lg"
              placeholder="cari produk..."
              onInput={(e) => {
                const inputValue = (e.target as HTMLInputElement).value;
                const charCount = inputValue.trim().length;
                charCount >= 2 ? setKeyword(inputValue) : setKeyword("");
              }}
            />
          </Form.Item>
        </Form>

        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
          <Button
            className="tw-text-accent-primary tw-border-text-secondary tw-bg-background-200 tw-text-xs"
            onClick={() => navigate(linkPage.MARKETPLACE_CHECKOUT)}
          >
            <div className="tw-flex tw-flex tw-justify-center tw-items-center">
              <FaCartPlus /> <div className="tw-ml-2">Keranjang</div>{" "}
              {Array.isArray(carts) && carts.length > 0 && (
                <Badge
                  className="tw-absolute tw-top-[-8px] tw-right-[-8px]"
                  style={{ backgroundColor: "#52c41a" }}
                  count={carts.length}
                />
              )}
            </div>
          </Button>
          <Button
            className="tw-text-accent-primary tw-border-text-secondary tw-bg-background-200 tw-text-xs"
            onClick={() => navigate(linkPage.MARKETPLACE_HISTORY)}
          >
            <div className="tw-flex tw-flex tw-justify-center tw-items-center">
              <FaList /> <div className="tw-ml-2">Pesanan</div>
            </div>
          </Button>
          <Button
            className="tw-text-accent-primary tw-border-text-secondary tw-bg-background-200 tw-text-xs"
            onClick={() => setPopupFilter(true)}
          >
            <div className="tw-flex tw-flex tw-justify-center tw-items-center">
              <FaFilter /> <div className="tw-ml-2">Filter</div>
            </div>
          </Button>
        </div>

        {isPending ? (
          <div className="tw-text-center tw-mt-8">
            <Spiner />
          </div>
        ) : data && data.length > 0 ? (
          <Masonry
            breakpointCols={{ default: 2 }}
            className="my-masonry-grid tw-mt-6"
            columnClassName="my-masonry-grid_column"
          >
            {data &&
              data.map((card: Product) => {
                const currentTime = new Date().toLocaleTimeString("id-ID", {
                  hour12: false,
                });
                const openTime = moment(card.openTime, "HH:mm:ss").format(
                  "HH.mm.ss"
                );
                const closeTime = moment(card.closeTime, "HH:mm:ss").format(
                  "HH.mm.ss"
                );

                const spansMidnight = closeTime < openTime;

                let isOpen;
                if (spansMidnight) {
                  isOpen = currentTime >= openTime || currentTime < closeTime;
                } else {
                  isOpen = currentTime >= openTime && currentTime < closeTime;
                }

                return (
                  isOpen && (
                    <div
                      key={card.id}
                      className="tw-p-2 card-body"
                      // style={{ background: "transparent" }}
                    >
                      <div className="tw-rounded-xl tw-overflow-hidden tw-break-inside-avoid">
                        {/* ⏳ Skeleton Loader */}
                        {!imgLoaded && (
                          <div className="tw-w-full tw-h-40 tw-bg-zinc-800 tw-animate-pulse tw-rounded-xl" />
                        )}
                        <div
                          className={`tw-rounded-xl tw-overflow-hidden tw-w-full tw-h-40 tw-shadow-md ${
                            imgLoaded ? "tw-block" : "tw-hidden"
                          }`}
                        >
                          <img
                            src={`https://sip2.darululumkalbar.com//assets/mplace/${card.productImage}`}
                            alt={card.productName}
                            className={`tw-w-full tw-h-full tw-object-cover`}
                            // loading="lazy"
                            onClick={() => {
                              setPopupImg(true);
                              setSelectedImg(
                                `https://sip2.darululumkalbar.com//assets/mplace/${card.productImage}`
                              );
                            }}
                            onLoad={() => setImgLoaded(true)}
                          />
                        </div>

                        <div className="tw-pt-2 tw-pb-0 tw-px-1 tw-space-y-1">
                          <div
                            className={`tw-text-xs tw-text-text-primary ${
                              clampedId === card.id ? "" : "tw-line-clamp-2"
                            }`}
                            onClick={() =>
                              setClampedId((prev) =>
                                prev === card.id ? null : card.id
                              )
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {card.productName}
                          </div>
                          <div className="tw-text-lg tw-font-bold tw-text-text-primary">
                            {toRp(card.sellPrice)}
                          </div>

                          {/* <div className="tw-text-[10px] tw-text-text-secondary">
                            tersisa: {card.sisa_stok}
                          </div> */}

                          <div className="tw-flex tw-items-center tw-text-green-500 tw-text-[10px] tw-mt-2">
                            <HiBuildingStorefront className="tw-mr-1" />
                            {card.merchantName}
                          </div>

                          <div className="tw-flex tw-gap-1 tw-mt-4">
                            <Button
                              className={`tw-flex-1 tw-p-2 tw-text-background-200 tw-border-none tw-rounded-lg tw- tw-text-xs ${
                                card.stockReady === "Y"
                                  ? "tw-bg-primary"
                                  : "tw-bg-red-700"
                              }`}
                              disabled={card.stockReady === "N"}
                              onClick={() => onAddOnCart(card as Product)}
                            >
                              {card.stockReady === "Y" ? (
                                <div className="tw-flex tw-flex tw-justify-center tw-items-center">
                                  <FaCartPlus />{" "}
                                  <div className="tw-ml-2">Keranjang</div>
                                </div>
                              ) : (
                                "Tidak Tersedia"
                              )}
                            </Button>
                            <Button className="tw-flex-2 tw-bg-green-500 tw-text-background-200 tw-p-2 tw-border-none tw-rounded-lg tw-flex tw-justify-center tw-text-xs">
                              <FaWhatsapp />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                );
              })}
          </Masonry>
        ) : (
          <div className="tw-mt-8 tw-p-8 tw-text-center card-body">
            <FcFinePrint size={50} />
            <div>Data tidak tersedia</div>
          </div>
        )}
      </div>

      <PopupCustom
        title="Filter Produk"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <div className="">
          <div className="tw-text-accent-primary tw-text-xs tw-mb-1">
            Pilih Jenis
          </div>
          <Select
            className=" tw-text-base"
            placeholder="Pilih Jenis"
            defaultValue={jenis}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setJenis(e)}
            options={[
              { value: "*", label: "Semua" },
              { value: "Makanan", label: "Makanan" },
              { value: "Minuman", label: "Minuman" },
              { value: "Pakaian", label: "Pakaian" },
              { value: "Herbal", label: "Herbal" },
              { value: "Buku dan Kitab", label: "Buku dan Kitab" },
              { value: "Aksesoris", label: "Aksesoris" },
            ]}
          ></Select>

          <div className="tw-text-accent-primary tw-text-xs tw-mb-1 tw-mt-4">
            Pilih Toko
          </div>
          <Select
            className=" tw-text-base"
            placeholder="Pilih Toko"
            defaultValue={toko}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setToko(e)}
            options={[
              { value: "*", label: "Semua Toko" },
              ...(dataMerchant?.data?.map((item, idx) => ({
                value: item.id,
                label: item.name,
              })) ?? []),
            ]}
          ></Select>

          <Button
            onClick={() => setPopupFilter(false)}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl tw-bg-red-500 tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            Tutup
            {/* {isPendingUpdateBio ? "" : "Update"} */}
          </Button>
        </div>
      </PopupCustom>

      <PopupCustom
        title=""
        open={popupImg}
        onCancel={() => {
          setPopupImg(false);
          setSelectedImg("");
        }}
        slideUpModal={false}
      >
        <img
          src={selectedImg}
          alt="img"
          className={`tw-w-full tw-h-full tw-rounded-xl`}
          // loading="lazy"
          onLoad={() => setImgLoaded(true)}
        />
        <Button
          onClick={() => setPopupImg(false)}
          className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl tw-bg-red-500 tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
        >
          Tutup
        </Button>
      </PopupCustom>
    </>
  );
};

export default MarketplaceProductPage;
